<div data-spartan-page class="p-6 md:p-8 space-y-6"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page header -->
  <app-page-header
    eyebrow="Shopee"
    title="Shopee Dashboard"
    subtitle="Monitor and manage your Shopee orders and pre-alerts."
  >
    <div actions class="flex items-center gap-2">
      <button hlmBtn variant="outline" size="sm"
              routerLink="/dashboard">
        <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-3.5" />
        Back
      </button>
      <button hlmBtn variant="outline" size="sm"
              (click)="refreshData()"
              [disabled]="isLoading">
        <ng-icon hlm name="lucideRefreshCw"
                 class="mr-1.5 size-3.5"
                 [class.animate-spin]="isLoading" />
        {{ isLoading ? 'Refreshing…' : 'Refresh' }}
      </button>
    </div>
  </app-page-header>

  <!-- Alerts -->
  @if (errorMessage) {
    <app-inline-alert tone="danger" [message]="errorMessage" />
  }
  @if (successMessage) {
    <app-inline-alert tone="success" [message]="successMessage" />
  }

  <!-- KPI tiles — 4 columns -->
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

    <!-- Total Orders -->
    <div hlmCard>
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Orders</div>
          @if (metricsLoading) {
            <div class="mt-2"><app-skeleton [rows]="1" rowHeight="h-7" /></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ metrics.totalOrders | number }}</div>
          }
          <div class="flex items-center gap-1 mt-1.5">
            <span class="text-[11px] text-muted-foreground">Active orders</span>
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
          <ng-icon hlm name="lucidePackage" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Active Pre-Alerts -->
    <div hlmCard>
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground uppercase tracking-wider">Active Pre-Alerts</div>
          @if (metricsLoading) {
            <div class="mt-2"><app-skeleton [rows]="1" rowHeight="h-7" /></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ metrics.activePreAlerts | number }}</div>
          }
          <div class="flex items-center gap-1 mt-1.5">
            <span class="text-[11px] text-muted-foreground">In transit</span>
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md bg-accent-light text-accent shrink-0">
          <ng-icon hlm name="lucideBell" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Pending Handovers -->
    <div hlmCard>
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground uppercase tracking-wider">Pending Handovers</div>
          @if (metricsLoading) {
            <div class="mt-2"><app-skeleton [rows]="1" rowHeight="h-7" /></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ metrics.pendingHandovers | number }}</div>
          }
          <div class="flex items-center gap-1 mt-1.5">
            <span class="text-[11px] text-muted-foreground">Last 24 hours</span>
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 shrink-0">
          <ng-icon hlm name="lucideClock" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Completed Today -->
    <div hlmCard>
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground uppercase tracking-wider">Completed Today</div>
          @if (metricsLoading) {
            <div class="mt-2"><app-skeleton [rows]="1" rowHeight="h-7" /></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ metrics.completedToday | number }}</div>
          }
          <div class="flex items-center gap-1 mt-1.5">
            <span class="text-[11px] text-green-700 dark:text-green-400 font-medium">Active</span>
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 shrink-0">
          <ng-icon hlm name="lucideCheckCircle" class="size-4" />
        </span>
      </div>
    </div>

  </div>

  <!-- Quick Actions -->
  <div hlmCard>
    <div class="px-5 pt-4 pb-3 border-b border-border">
      <h3 class="text-base font-semibold leading-tight flex items-center gap-2">
        <ng-icon hlm name="lucideZap" class="size-4 text-accent" />
        Quick Actions
      </h3>
    </div>
    <div class="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <button hlmBtn variant="outline" size="sm"
              class="flex flex-col items-center py-4 gap-2 h-auto min-h-[80px] w-full"
              routerLink="/dashboard/orders">
        <span class="grid place-items-center w-8 h-8 rounded-md bg-accent-light text-accent">
          <ng-icon hlm name="lucidePackage" class="size-4" />
        </span>
        <div class="text-center">
          <div class="text-sm font-medium">View Orders</div>
          <div class="text-xs text-muted-foreground">Browse all orders</div>
        </div>
      </button>
      <button hlmBtn variant="outline" size="sm"
              class="flex flex-col items-center py-4 gap-2 h-auto min-h-[80px] w-full"
              routerLink="/dashboard/pre-alerts">
        <span class="grid place-items-center w-8 h-8 rounded-md bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300">
          <ng-icon hlm name="lucideBell" class="size-4" />
        </span>
        <div class="text-center">
          <div class="text-sm font-medium">Pre-Alerts</div>
          <div class="text-xs text-muted-foreground">Manage pre-alerts</div>
        </div>
      </button>
      <button hlmBtn variant="outline" size="sm"
              class="flex flex-col items-center py-4 gap-2 h-auto min-h-[80px] w-full"
              routerLink="/dashboard/shopee-excel-upload">
        <span class="grid place-items-center w-8 h-8 rounded-md bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300">
          <ng-icon hlm name="lucideUpload" class="size-4" />
        </span>
        <div class="text-center">
          <div class="text-sm font-medium">Import LM</div>
          <div class="text-xs text-muted-foreground">Excel upload</div>
        </div>
      </button>
      <button hlmBtn variant="outline" size="sm"
              class="flex flex-col items-center py-4 gap-2 h-auto min-h-[80px] w-full"
              routerLink="/dashboard/mobile-scanner">
        <span class="grid place-items-center w-8 h-8 rounded-md bg-accent-light text-accent">
          <ng-icon hlm name="lucideScanLine" class="size-4" />
        </span>
        <div class="text-center">
          <div class="text-sm font-medium">Mobile Scanner</div>
          <div class="text-xs text-muted-foreground">Scan cartons</div>
        </div>
      </button>
    </div>
  </div>

  <!-- Lower row: Recent Activity + Tasks/Status -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

    <!-- Recent Activity -->
    <div hlmCard class="lg:col-span-2">
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold leading-tight">Recent Activity</h3>
          <p class="text-xs text-muted-foreground mt-0.5">Last 24 hours</p>
        </div>
        <button hlmBtn variant="ghost" size="sm"
                (click)="loadMoreActivity()"
                [disabled]="loadingMoreActivity">
          @if (loadingMoreActivity) {
            <ng-icon hlm name="lucideLoader" class="mr-1.5 size-3.5 animate-spin" />
          }
          Load more
          <ng-icon hlm name="lucideChevronRight" class="ml-1 size-3.5" />
        </button>
      </div>

      <div class="px-5 py-3">
        @if (activityLoading) {
          <app-skeleton [rows]="5" />
        } @else if (recentActivity.length === 0) {
          <div class="py-10 flex flex-col items-center text-center gap-3">
            <span class="grid place-items-center w-12 h-12 rounded-xl bg-surface-raised text-muted-foreground">
              <ng-icon hlm name="lucideActivity" class="size-6" />
            </span>
            <div class="text-sm font-semibold">No Recent Activity</div>
            <p class="text-xs text-muted-foreground">Recent system activities will appear here.</p>
          </div>
        } @else {
          <ol class="relative space-y-0">
            @for (activity of recentActivity; track trackActivityById($index, activity)) {
              <li class="relative pl-8 py-2.5 group">
                <span class="absolute left-2 top-3 w-px bottom-0 bg-border" aria-hidden="true"></span>
                <span class="absolute left-0 top-2.5 grid place-items-center w-5 h-5 rounded-full bg-surface ring-1 ring-border text-muted-foreground">
                  <ng-icon hlm
                           [name]="activity.type === 'order' ? 'lucidePackage' : 'lucideBell'"
                           class="size-3" />
                </span>
                <div class="flex items-baseline justify-between gap-3">
                  <p class="text-sm">
                    <span class="font-medium text-foreground">{{ activity.user || 'System' }}</span>
                    <span class="text-muted-foreground"> — {{ activity.description }}</span>
                  </p>
                  <span class="text-[11px] tabular-nums text-muted-foreground shrink-0">
                    {{ formatTimestamp(activity.timestamp) }}
                  </span>
                </div>
              </li>
            }
          </ol>
        }
      </div>
    </div>

    <!-- Right column: Tasks + System status -->
    <div class="space-y-4">

      <!-- Pending Tasks -->
      <div hlmCard>
        <div class="px-5 pt-4 pb-3 border-b border-border">
          <h3 class="text-base font-semibold leading-tight">Pending Tasks</h3>
          <p class="text-xs text-muted-foreground mt-0.5">Role-based action items</p>
        </div>
        <div class="p-5">
          @if (tasksLoading) {
            <app-skeleton [rows]="4" />
          } @else if (pendingTasks.length === 0) {
            <div class="py-6 flex flex-col items-center text-center gap-2">
              <ng-icon hlm name="lucideCheckCircle" class="size-8 text-green-600" />
              <p class="text-sm font-medium">All caught up!</p>
              <p class="text-xs text-muted-foreground">No pending tasks</p>
            </div>
          } @else {
            <div class="space-y-3">
              @for (task of pendingTasks; track task) {
                <div class="flex items-center gap-3">
                  <span class="w-2 h-2 rounded-full bg-amber-500 shrink-0"></span>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-foreground truncate">{{ task.title }}</p>
                    <p class="text-xs text-muted-foreground truncate">{{ task.description }}</p>
                  </div>
                  <button hlmBtn variant="ghost" size="sm"
                          class="h-7 w-7 p-0 shrink-0"
                          (click)="navigateToTask(task)"
                          title="Go to task">
                    <ng-icon hlm name="lucideArrowRight" class="size-3.5" />
                  </button>
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- System Status snapshot -->
      <div hlmCard>
        <div class="px-5 pt-4 pb-3 border-b border-border">
          <h3 class="text-base font-semibold leading-tight">System Status</h3>
        </div>
        <div class="p-5 space-y-3.5">
          <div class="flex items-center gap-3">
            <span class="w-2 h-2 rounded-full bg-green-500 shrink-0"></span>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium">Orders Processing</div>
              <div class="text-[11px] text-muted-foreground">{{ metrics.systemActivity }} active</div>
            </div>
            <app-status-badge variant="success" label="Active" />
          </div>
          <div class="flex items-center gap-3">
            <span class="w-2 h-2 rounded-full bg-green-500 shrink-0"></span>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium">Document System</div>
              <div class="text-[11px] text-muted-foreground">Operational</div>
            </div>
            <app-status-badge variant="success" label="Operational" />
          </div>
          <div class="flex items-center gap-3">
            <span class="w-2 h-2 rounded-full bg-green-500 shrink-0"></span>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium">Tracking Services</div>
              <div class="text-[11px] text-muted-foreground">Online</div>
            </div>
            <app-status-badge variant="success" label="Online" />
          </div>
        </div>
      </div>

    </div>
  </div>

</div>
